import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { getVaultMetadata, createSeniorToken, type AdvisorVaultMetadata } from '../lib/advisorApi';
import { formatDate, humanize } from '../lib/format';
import './advisor.css';

// The base URL of the no-login senior recording app. A window override lets the
// e2e/dev point it at the local senior app (:5198) without an env-typing dep.
const SENIOR_APP_BASE =
  (window as unknown as { LA_SENIOR_APP_BASE?: string }).LA_SENIOR_APP_BASE ??
  'https://record.legacyarchitect.io';

export function ClientDetailScreen() {
  const { vaultId = '' } = useParams<{ vaultId: string }>();
  const { api } = useAuth();
  const [data, setData] = useState<AdvisorVaultMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seniorLabel, setSeniorLabel] = useState('');
  const [seniorLink, setSeniorLink] = useState<string | null>(null);
  const [seniorBusy, setSeniorBusy] = useState(false);
  const [seniorErr, setSeniorErr] = useState<string | null>(null);

  async function generateSeniorLink(): Promise<void> {
    setSeniorBusy(true);
    setSeniorErr(null);
    setSeniorLink(null);
    try {
      const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      const res = await createSeniorToken(api, {
        vaultId,
        expiresAt,
        preSetLabel: seniorLabel.trim() === '' ? null : seniorLabel.trim(),
      });
      setSeniorLink(`${SENIOR_APP_BASE}/?t=${res.token}`);
    } catch {
      setSeniorErr('We could not generate a link. This vault must be in your book.');
    } finally {
      setSeniorBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await getVaultMetadata(api, vaultId);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setError('That vault is not in your book.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, vaultId]);

  const triggers = Object.entries(data?.triggerTypeDistribution ?? {});

  return (
    <div className="la-rise">
      <header className="page__head">
        <p className="la-label">
          <Link className="table__link" to="/clients">
            ← Clients
          </Link>
        </p>
        <h1 className="page__title">{data?.vaultName ?? 'Vault'}</h1>
        <p className="page__sub">
          {data === null ? '' : `Vault is ${humanize(data.vaultStatus)}.`}
        </p>
      </header>

      {error !== null && (
        <p className="auth__alert" role="alert">
          {error}
        </p>
      )}

      {data !== null && (
        <>
          <div className="stats">
            <div className="la-card stat">
              <p className="la-label">Messages</p>
              <p className="stat__value">{data.messageCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Sealed</p>
              <p className="stat__value">{data.sealedCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Drafts</p>
              <p className="stat__value">{data.draftCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Delivered</p>
              <p className="stat__value">{data.deliveredCount}</p>
            </div>
            <div className="la-card stat">
              <p className="la-label">Recipients</p>
              <p className="stat__value">{data.recipientCount}</p>
            </div>
          </div>

          <div className="la-card formCard">
            <p className="la-label">Check-in</p>
            <h2>{formatDate(data.nextCheckinDueAt)}</h2>
            <p>
              Last seen {formatDate(data.lastCheckinAt)}
              {data.checkinStatus === null ? '' : ` — ${humanize(data.checkinStatus)}`}. If a client
              stops checking in, their guardians are alerted before anything is released.
            </p>
          </div>

          <div className="la-card formCard">
            <p className="la-label">How their messages are triggered</p>
            {triggers.length === 0 ? (
              <p>No triggers set yet.</p>
            ) : (
              <div className="tableWrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Trigger</th>
                      <th>Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {triggers.map(([type, count]) => (
                      <tr key={type}>
                        <td className="table__name">{humanize(type)}</td>
                        <td>{count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="la-card formCard">
            <p className="la-label">Senior recording link</p>
            <h2>For a family member who can&rsquo;t use the app</h2>
            <p>
              Generate a one-tap recording link for a grandparent or other contributor who has no
              account. They open it, record a message, and it lands in this vault as a draft for the
              client to review. <strong>The client must approve the link before it works</strong> —
              it is inert until they do. Optionally label what it&rsquo;s for.
            </p>
            <label className="auth__field">
              <span className="auth__label">Label (optional)</span>
              <input
                className="auth__input"
                type="text"
                value={seniorLabel}
                placeholder="e.g. A message for Emma's graduation"
                onChange={(e) => setSeniorLabel(e.target.value)}
              />
            </label>
            {seniorErr !== null && (
              <p className="auth__alert" role="alert">
                {seniorErr}
              </p>
            )}
            {seniorLink !== null && (
              <p className="auth__notice" role="status" style={{ wordBreak: 'break-all' }}>
                Link ready (share it with the contributor):<br />
                <strong>{seniorLink}</strong>
                <br />
                Remember: the client approves it from their app before it will record.
              </p>
            )}
            <button
              type="button"
              className="la-btn-secondary"
              disabled={seniorBusy}
              onClick={() => void generateSeniorLink()}
            >
              {seniorBusy ? 'Generating…' : 'Generate recording link'}
            </button>
          </div>

          <p className="la-card privacyNote">
            Message titles and contents are deliberately withheld from this view — the API returns
            them as null. You can see that a message exists and when it is due. You cannot see what
            it says, and neither can anyone at this firm.
          </p>
        </>
      )}
    </div>
  );
}
